import { formatDateTime, formatDate, formatDateTimeFull } from "@/lib/format";
import type { ReactNode } from "react";
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { glopicApi } from "@/lib/glopic-api";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import ConfirmModal from "@/components/base/ConfirmModal";

interface UserOverview {
  current_points: number;
  total_points_earned: number;
  total_points_spent: number;
  points_log_count: number;
  generation_total: number;
  generation_success: number;
  generation_failed: number;
  generation_processing: number;
  latest_generation_at: string | null;
  order_count: number;
  paid_order_count: number;
  paid_amount_total: number;
  last_login_at: string | null;
}

interface UserAccount {
  user_type: "consumer" | "merchant";
  contact: string | null;
  phone_bound: boolean;
  email_bound: boolean;
  login_type: string | null;
  country_code: string | null;
  status: string;
  created_at: string;
  last_login_at: string | null;
  device_brand: string | null;
  device_platform: string | null;
  device_model: string | null;
}

interface UserVip {
  is_vip: boolean;
  vip_expire_time: string | null;
  has_active_subscription: boolean;
  package_id: string | null;
  package_name: string | null;
  subscription_id: string | null;
  subscription_status: number | null;
  subscription_status_label: string;
  subscription_start_time: string | null;
  subscription_expires_at: string | null;
  source_order_id: string | null;
}

interface PointsEntry {
  id: number;
  delta: number;
  balance_after: number;
  reason: string;
  ref_id: string | null;
  created_at: string;
}

interface GenEntry {
  gen_id: string;
  user_type: "consumer" | "merchant" | null;
  prompt: string | null;
  status: string;
  task_status: string;
  image_url: string | null;
  display_image_url: string | null;
  original_image_url: string | null;
  image_url_type: string | null;
  model_id: string | null;
  provider: string | null;
  provider_model: string | null;
  source_entry: string | null;
  entry_id: string | null;
  tool_id: string | null;
  generation_operation: string | null;
  cost_points: number;
  failure_message: string | null;
  created_at: string;
  finished_at: string | null;
}

interface SubscriptionOrderEntry {
  order_id: string;
  package_id: string;
  package_name: string | null;
  amount: number;
  currency: string;
  pay_channel: string;
  payment_status: number;
  payment_status_label: string;
  paid_at: string | null;
  order_created_at: string;
  subscription_id: string | null;
  subscription_status: number | null;
  subscription_status_label: string;
  subscription_start_time: string | null;
  subscription_expires_at: string | null;
  is_subscription_success: boolean;
  abnormal_reason: string | null;
}

interface AiUserDetail {
  user_id: string;
  user_type: "consumer" | "merchant";
  nickname: string | null;
  avatar: string | null;
  contact: string | null;
  points: number;
  is_vip: boolean;
  vip_expire_time: string | null;
  status: string;
  login_type: string | null;
  country_code: string | null;
  last_login_at: string | null;
  created_at: string;
  device_brand: string | null;
  total_generations: number;
  overview?: UserOverview;
  account?: UserAccount;
  vip?: UserVip;
  points_log?: PointsEntry[];
  recent_generations?: GenEntry[];
  subscription_orders?: SubscriptionOrderEntry[];
}

const reasonLabels: Record<string, string> = {
  daily_checkin: "每日签到",
  vip_package_points: "VIP 礼包",
  generate_image: "生成图片",
  invite_reward: "邀请奖励",
  admin_adjust: "管理员调整",
};

const operationLabels: Record<string, string> = {
  text_to_image: "文生图",
  image_to_image: "图生图",
  image_edit: "图片编辑",
};

const statusLabels: Record<string, { label: string; cls: string }> = {
  active: { label: "正常", cls: "bg-emerald-500/10 text-emerald-500" },
  suspended: { label: "冻结", cls: "bg-amber-500/10 text-amber-500" },
  banned: { label: "封禁", cls: "bg-red-500/10 text-red-500" },
};

const userTypeLabels = {
  consumer: { label: "C端用户", cls: "bg-blue-500/10 text-blue-600" },
  merchant: { label: "商家用户", cls: "bg-violet-500/10 text-violet-600" },
} as const;

type TabKey = "overview" | "account" | "generations" | "points" | "vip";

export default function AiUserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canAccess } = useAdminAuth();
  const [user, setUser] = useState<AiUserDetail | null>(null);
  const [pointsLog, setPointsLog] = useState<PointsEntry[]>([]);
  const [generations, setGenerations] = useState<GenEntry[]>([]);
  const [subscriptionOrders, setSubscriptionOrders] = useState<SubscriptionOrderEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [pointsAdjust, setPointsAdjust] = useState("");
  const [pointsReason, setPointsReason] = useState("");
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [banConfirmOpen, setBanConfirmOpen] = useState(false);
  const [statusReason, setStatusReason] = useState("");
  const [pointsConfirmOpen, setPointsConfirmOpen] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await glopicApi.get(`/users/${id}`);
      const data = response.data.data as AiUserDetail;
      setUser(data);
      setPointsLog(data.points_log ?? []);
      setGenerations(data.recent_generations ?? []);
      setSubscriptionOrders(data.subscription_orders ?? []);
    } catch (err) {
      console.error("AI user detail error:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const executeToggleStatus = async () => {
    if (!user) return;
    const newStatus = user.status === "active" ? "banned" : "active";
    try {
      await glopicApi.patch(`/users/${user.user_id}/status`, {
        status: newStatus,
        reason: statusReason,
      });
      fetchData();
    } catch (err) {
      console.error("Toggle status error:", err);
    }
    setBanConfirmOpen(false);
    setStatusReason("");
  };

  const executeAdjustPoints = async () => {
    if (!user || !pointsAdjust) return;
    const delta = parseInt(pointsAdjust, 10);
    if (!delta) return;
    try {
      await glopicApi.post(`/users/${user.user_id}/adjust-points`, {
        delta,
        reason: pointsReason.trim(),
      });
      setShowPointsModal(false);
      setPointsAdjust("");
      setPointsReason("");
      setPointsConfirmOpen(false);
      fetchData();
    } catch (err) {
      console.error("Adjust points error:", err);
    }
  };

  const canModify = canAccess("ai_users");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-20">
        <i className="ri-user-unfollow-line text-4xl text-text-muted mb-3" />
        <p className="text-text-secondary">用户不存在或已被删除</p>
        <button onClick={() => navigate("/ai/users")} className="mt-4 text-brand text-sm hover:underline cursor-pointer">
          返回用户列表
        </button>
      </div>
    );
  }

  const overview = user.overview ?? buildFallbackOverview(user, pointsLog, generations, subscriptionOrders);
  const account = user.account ?? buildFallbackAccount(user);
  const vip = user.vip ?? buildFallbackVip(user);
  const statusCfg = statusLabels[user.status] || statusLabels.active;
  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: "overview", label: "概览" },
    { key: "account", label: "账号信息" },
    { key: "generations", label: "生成记录", count: overview.generation_total },
    { key: "points", label: "积分流水", count: overview.points_log_count },
    { key: "vip", label: "VIP订单", count: overview.order_count },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate("/ai/users")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-surface text-text-secondary transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-text-primary">用户详情</h2>
            <p className="text-xs text-text-muted font-mono truncate">{user.user_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={fetchData}
            className="w-9 h-9 rounded-lg flex items-center justify-center text-text-secondary border border-surface-border hover:bg-surface transition-colors cursor-pointer"
            title="刷新数据"
          >
            <i className="ri-refresh-line" />
          </button>
          {canModify && (
            <button
              onClick={() => setShowPointsModal(true)}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-brand/10 text-brand hover:bg-brand/20 transition-colors cursor-pointer"
            >
              调整积分
            </button>
          )}
          {canModify && (
            <button
              onClick={() => {
                const reason = window.prompt("请输入账号状态变更原因（将写入不可变审计）")?.trim();
                if (!reason) return;
                setStatusReason(reason);
                setBanConfirmOpen(true);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                user.status === "active"
                  ? "bg-red-500/10 text-red-500 hover:bg-red-500/20"
                  : "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20"
              }`}
            >
              {user.status === "active" ? "封禁账号" : "解封账号"}
            </button>
          )}
        </div>
      </div>

      <section className="bg-white rounded-lg border border-surface-border p-5">
        <div className="flex flex-col lg:flex-row lg:items-start gap-5">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            {user.avatar ? (
              <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center text-brand font-semibold text-xl shrink-0">
                {(user.nickname || "?")[0].toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h3 className="text-lg font-semibold text-text-primary truncate">{user.nickname || user.user_id}</h3>
                <Badge className={userTypeLabels[user.user_type].cls}>{userTypeLabels[user.user_type].label}</Badge>
                <Badge className={statusCfg.cls}>{statusCfg.label}</Badge>
                {vip.is_vip && <Badge className="bg-amber-500/10 text-amber-600"><i className="ri-vip-crown-line" />VIP</Badge>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-2 text-xs text-text-muted">
                <span>注册：{formatDateTimeFull(user.created_at)}</span>
                <span>最近登录：{formatDateTimeFull(account.last_login_at)}</span>
                <span>登录方式：{account.login_type || "-"}</span>
                <span>地区：{account.country_code || "-"}</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-3 lg:min-w-[420px]">
            <MiniMetric label="积分余额" value={formatNumber(overview.current_points)} />
            <MiniMetric label="生成总数" value={formatNumber(overview.generation_total)} />
            <MiniMetric label="VIP状态" value={vip.is_vip ? "VIP" : "普通"} />
            <MiniMetric label="付费金额" value={formatMoney(overview.paid_amount_total)} />
          </div>
        </div>
      </section>

      <div className="flex items-center gap-1 bg-white rounded-lg border border-surface-border p-1 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              activeTab === t.key ? "bg-brand text-white" : "text-text-secondary hover:bg-surface"
            }`}
          >
            {t.label}{typeof t.count === "number" ? ` (${formatNumber(t.count)})` : ""}
          </button>
        ))}
      </div>

      {activeTab === "overview" && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <MetricCard label="当前积分" value={formatNumber(overview.current_points)} icon="ri-coins-line" />
            <MetricCard label="累计获得 / 消耗" value={`${formatNumber(overview.total_points_earned)} / ${formatNumber(overview.total_points_spent)}`} icon="ri-exchange-dollar-line" />
            <MetricCard label="生成成功 / 失败" value={`${formatNumber(overview.generation_success)} / ${formatNumber(overview.generation_failed)}`} icon="ri-image-ai-line" />
            <MetricCard label="付费订单 / 金额" value={`${formatNumber(overview.paid_order_count)} / ${formatMoney(overview.paid_amount_total)}`} icon="ri-bank-card-line" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
            <InfoPanel title="账号摘要">
              <InfoRow label="联系方式" value={account.contact || "-"} mono />
              <InfoRow label="手机号 / 邮箱" value={`${account.phone_bound ? "已绑定" : "未绑定"} / ${account.email_bound ? "已绑定" : "未绑定"}`} />
              <InfoRow label="最近设备" value={[account.device_brand, account.device_model].filter(Boolean).join(" / ") || "-"} />
              <InfoRow label="最近生成" value={formatDateTimeFull(overview.latest_generation_at)} />
            </InfoPanel>
            <InfoPanel title="VIP 摘要">
              <InfoRow label="套餐" value={vip.package_name || vip.package_id || "-"} />
              <InfoRow label="订阅状态" value={vip.subscription_status_label || "-"} />
              <InfoRow label="VIP 到期" value={formatDate(vip.vip_expire_time || vip.subscription_expires_at)} />
              <InfoRow label="来源订单" value={vip.source_order_id || "-"} mono />
            </InfoPanel>
          </div>
          <RecentTables
            generations={generations.slice(0, 5)}
            pointsLog={pointsLog.slice(0, 5)}
            onOpenGeneration={(genId) => navigate(`/ai/generations/${genId}`)}
            onOpenTab={setActiveTab}
          />
        </div>
      )}

      {activeTab === "account" && (
        <InfoPanel title="账号信息">
          <InfoRow label="用户ID" value={user.user_id} mono />
          <InfoRow label="用户类型" value={userTypeLabels[user.user_type].label} />
          <InfoRow label="昵称" value={user.nickname || "-"} />
          <InfoRow label="联系方式" value={account.contact || "-"} mono />
          <InfoRow label="手机号绑定" value={account.phone_bound ? "已绑定" : "未绑定"} />
          <InfoRow label="邮箱绑定" value={account.email_bound ? "已绑定" : "未绑定"} />
          <InfoRow label="登录方式" value={account.login_type || "-"} />
          <InfoRow label="账号状态" value={statusCfg.label} />
          <InfoRow label="地区" value={account.country_code || "-"} />
          <InfoRow label="注册时间" value={formatDateTimeFull(account.created_at)} />
          <InfoRow label="最近登录" value={formatDateTimeFull(account.last_login_at)} />
          <InfoRow label="最近设备品牌" value={account.device_brand || "-"} />
          <InfoRow label="最近设备型号" value={account.device_model || "-"} />
          <InfoRow label="最近设备平台" value={account.device_platform || "-"} />
        </InfoPanel>
      )}

      {activeTab === "generations" && (
        <GenerationsTable generations={generations} onOpen={(genId) => navigate(`/ai/generations/${genId}`)} />
      )}

      {activeTab === "points" && (
        <PointsTable pointsLog={pointsLog} />
      )}

      {activeTab === "vip" && (
        <div className="space-y-5">
          <InfoPanel title="当前 VIP">
            <InfoRow label="是否 VIP" value={vip.is_vip ? "是" : "否"} />
            <InfoRow label="套餐" value={vip.package_name || vip.package_id || "-"} />
            <InfoRow label="订阅状态" value={vip.subscription_status_label || "-"} />
            <InfoRow label="开始时间" value={formatDateTimeFull(vip.subscription_start_time)} />
            <InfoRow label="到期时间" value={formatDateTimeFull(vip.vip_expire_time || vip.subscription_expires_at)} />
            <InfoRow label="订阅ID" value={vip.subscription_id || "-"} mono />
            <InfoRow label="来源订单" value={vip.source_order_id || "-"} mono />
          </InfoPanel>
          <SubscriptionOrdersTable orders={subscriptionOrders} />
        </div>
      )}

      {showPointsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-text-primary mb-4">调整积分</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-text-secondary mb-1">调整数量（正数增加，负数减少）</label>
                <input
                  type="number"
                  value={pointsAdjust}
                  onChange={(e) => setPointsAdjust(e.target.value)}
                  placeholder="如: +100 或 -50"
                  className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm text-text-secondary mb-1">备注原因</label>
                <input
                  type="text"
                  value={pointsReason}
                  onChange={(e) => setPointsReason(e.target.value)}
                  placeholder="请输入调整原因（必填）"
                  className="w-full h-10 px-3 rounded-lg bg-surface text-sm text-text-primary border border-surface-border focus:border-brand/50 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => { setShowPointsModal(false); setPointsAdjust(""); setPointsReason(""); }}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium text-text-secondary border border-surface-border hover:bg-surface transition-colors cursor-pointer"
              >
                取消
              </button>
              <button
                onClick={() => pointsAdjust && pointsReason.trim() && setPointsConfirmOpen(true)}
                disabled={!pointsAdjust || !pointsReason.trim()}
                className="flex-1 py-2.5 rounded-lg text-sm font-medium text-white bg-brand hover:bg-brand/90 disabled:opacity-40 transition-colors cursor-pointer"
              >
                确认调整
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={banConfirmOpen}
        title={user.status === "active" ? "封禁账号" : "解封账号"}
        description={
          user.status === "active"
            ? `确认封禁用户 "${user.nickname || user.user_id}"？该用户将无法使用 AI 生图功能。`
            : `确认解封用户 "${user.nickname || user.user_id}"？解封后恢复正常使用。`
        }
        confirmText={user.status === "active" ? "确认封禁" : "确认解封"}
        confirmDanger={user.status === "active"}
        onConfirm={executeToggleStatus}
        onCancel={() => { setBanConfirmOpen(false); setStatusReason(""); }}
      />

      <ConfirmModal
        open={pointsConfirmOpen}
        title="确认调整积分"
        description={`确认为用户 "${user.nickname || user.user_id}" ${parseInt(pointsAdjust || "0", 10) > 0 ? "增加" : "扣除"} ${Math.abs(parseInt(pointsAdjust || "0", 10))} 积分？调整后余额为 ${user.points + parseInt(pointsAdjust || "0", 10)}。`}
        confirmText="确认调整"
        onConfirm={executeAdjustPoints}
        onCancel={() => setPointsConfirmOpen(false)}
      />
    </div>
  );
}

function buildFallbackOverview(
  user: AiUserDetail,
  pointsLog: PointsEntry[],
  generations: GenEntry[],
  orders: SubscriptionOrderEntry[],
): UserOverview {
  return {
    current_points: user.points,
    total_points_earned: pointsLog.filter((p) => p.delta > 0).reduce((sum, p) => sum + p.delta, 0),
    total_points_spent: Math.abs(pointsLog.filter((p) => p.delta < 0).reduce((sum, p) => sum + p.delta, 0)),
    points_log_count: pointsLog.length,
    generation_total: user.total_generations ?? generations.length,
    generation_success: generations.filter((g) => g.task_status === "success").length,
    generation_failed: generations.filter((g) => g.task_status === "failed").length,
    generation_processing: generations.filter((g) =>
      ["created", "queued", "generating", "auditing", "pending", "processing"].includes(g.task_status)
    ).length,
    latest_generation_at: generations[0]?.created_at ?? null,
    order_count: orders.length,
    paid_order_count: orders.filter((o) => o.payment_status === 1).length,
    paid_amount_total: orders.filter((o) => o.payment_status === 1).reduce((sum, o) => sum + Number(o.amount || 0), 0),
    last_login_at: user.last_login_at,
  };
}

function buildFallbackAccount(user: AiUserDetail): UserAccount {
  return {
    user_type: user.user_type,
    contact: user.contact,
    phone_bound: Boolean(user.contact),
    email_bound: Boolean(user.contact && user.contact.includes("@")),
    login_type: user.login_type,
    country_code: user.country_code,
    status: user.status,
    created_at: user.created_at,
    last_login_at: user.last_login_at,
    device_brand: user.device_brand,
    device_platform: null,
    device_model: null,
  };
}

function buildFallbackVip(user: AiUserDetail): UserVip {
  return {
    is_vip: user.is_vip,
    vip_expire_time: user.vip_expire_time,
    has_active_subscription: user.is_vip,
    package_id: null,
    package_name: null,
    subscription_id: null,
    subscription_status: null,
    subscription_status_label: user.is_vip ? "已开通" : "未开通",
    subscription_start_time: null,
    subscription_expires_at: user.vip_expire_time,
    source_order_id: null,
  };
}

function Badge({ className, children }: { className: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-surface/60 px-3 py-2 min-w-0">
      <p className="text-[11px] text-text-muted mb-1">{label}</p>
      <p className="text-sm font-semibold text-text-primary truncate">{value}</p>
    </div>
  );
}

function MetricCard({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <div className="bg-white rounded-lg border border-surface-border p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-text-muted text-xs mb-1">{label}</p>
          <p className="text-xl font-semibold text-text-primary truncate">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
          <i className={icon} />
        </div>
      </div>
    </div>
  );
}

function InfoPanel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-white rounded-lg border border-surface-border p-5">
      <h4 className="text-sm font-semibold text-text-primary mb-4">{title}</h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        {children}
      </div>
    </section>
  );
}

function InfoRow({ label, value, mono = false }: { label: string; value: string | number; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 text-sm min-w-0">
      <span className="text-text-muted shrink-0">{label}</span>
      <span className={`text-text-primary text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{value}</span>
    </div>
  );
}

function RecentTables({
  generations,
  pointsLog,
  onOpenGeneration,
  onOpenTab,
}: {
  generations: GenEntry[];
  pointsLog: PointsEntry[];
  onOpenGeneration: (genId: string) => void;
  onOpenTab: (tab: TabKey) => void;
}) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
      <section className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <PanelHeader title="最近生成记录" action="查看全部" onAction={() => onOpenTab("generations")} />
        <CompactGenerations generations={generations} onOpen={onOpenGeneration} />
      </section>
      <section className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <PanelHeader title="最近积分流水" action="查看全部" onAction={() => onOpenTab("points")} />
        <CompactPoints pointsLog={pointsLog} />
      </section>
    </div>
  );
}

function PanelHeader({ title, action, onAction }: { title: string; action: string; onAction: () => void }) {
  return (
    <div className="h-11 px-4 border-b border-surface-border flex items-center justify-between">
      <h4 className="text-sm font-semibold text-text-primary">{title}</h4>
      <button onClick={onAction} className="text-xs text-brand hover:underline cursor-pointer">{action}</button>
    </div>
  );
}

function CompactGenerations({ generations, onOpen }: { generations: GenEntry[]; onOpen: (genId: string) => void }) {
  if (generations.length === 0) {
    return <EmptyState icon="ri-image-ai-line" text="暂无生成记录" />;
  }
  return (
    <div className="divide-y divide-surface-border">
      {generations.map((g) => (
        <button key={g.gen_id} onClick={() => onOpen(g.gen_id)} className="w-full px-4 py-3 text-left hover:bg-surface/40 cursor-pointer">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-text-primary truncate">{g.prompt || g.gen_id}</p>
            <GenStatusBadge status={g.task_status} />
          </div>
          <p className="text-xs text-text-muted mt-1">{formatDateTime(g.created_at)} · {g.cost_points} pts</p>
        </button>
      ))}
    </div>
  );
}

function CompactPoints({ pointsLog }: { pointsLog: PointsEntry[] }) {
  if (pointsLog.length === 0) {
    return <EmptyState icon="ri-coins-line" text="暂无积分流水" />;
  }
  return (
    <div className="divide-y divide-surface-border">
      {pointsLog.map((p) => (
        <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm text-text-primary truncate">{reasonLabels[p.reason] || p.reason}</p>
            <p className="text-xs text-text-muted">{formatDateTime(p.created_at)} · 余额 {p.balance_after}</p>
          </div>
          <span className={`font-mono font-semibold text-sm ${p.delta > 0 ? "text-emerald-500" : "text-red-500"}`}>
            {p.delta > 0 ? "+" : ""}{p.delta}
          </span>
        </div>
      ))}
    </div>
  );
}

function GenerationsTable({ generations, onOpen }: { generations: GenEntry[]; onOpen: (genId: string) => void }) {
  return (
    <TableShell empty={generations.length === 0} emptyIcon="ri-image-ai-line" emptyText="暂无生成记录" colSpan={10}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border bg-surface/50">
            <Th>预览</Th>
            <Th>任务ID</Th>
            <Th>提示词</Th>
            <Th>状态</Th>
            <Th>用户类型</Th>
            <Th>来源/入口/工具</Th>
            <Th>功能/模型</Th>
            <Th>消耗</Th>
            <Th>时间</Th>
            <Th align="right">操作</Th>
          </tr>
        </thead>
        <tbody>
          {generations.map((g) => (
            <tr key={g.gen_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors h-9">
              <Td>
                {g.display_image_url || g.image_url ? (
                  <ThumbImage src={(g.display_image_url ?? g.image_url) as string} />
                ) : (
                  <span className="text-text-muted">-</span>
                )}
              </Td>
              <Td mono>{g.gen_id.slice(-10)}</Td>
              <Td><p className="truncate max-w-[260px]" title={g.prompt || "-"}>{g.prompt || "-"}</p></Td>
              <Td><GenStatusBadge status={g.task_status} /></Td>
              <Td>
                <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] ${g.user_type === "merchant" ? "bg-violet-500/10 text-violet-600" : "bg-blue-500/10 text-blue-600"}`}>
                  {g.user_type === "merchant" ? "商家用户" : g.user_type === "consumer" ? "C端用户" : "未知类型"}
                </span>
              </Td>
              <Td>
                <div className="text-xs text-text-muted whitespace-nowrap">来源: {g.source_entry || "-"}</div>
                <div className="text-xs text-text-muted whitespace-nowrap">入口: {g.entry_id || "-"}</div>
                <div className="text-xs text-text-muted whitespace-nowrap">工具: {g.tool_id || "-"}</div>
              </Td>
              <Td>{operationLabels[g.generation_operation || ""] || g.generation_operation || g.model_id || "-"}</Td>
              <Td mono>{g.cost_points} pts</Td>
              <Td>{formatDateTime(g.created_at)}</Td>
              <Td align="right"><button onClick={() => onOpen(g.gen_id)} className="text-brand text-xs hover:underline cursor-pointer">详情</button></Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function ThumbImage({ src }: { src: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span className="flex h-10 w-10 items-center justify-center rounded bg-surface text-text-muted">
        <i className="ri-image-close-line" />
      </span>
    );
  }
  return <img src={src} alt="" className="h-10 w-10 rounded object-cover" onError={() => setFailed(true)} />;
}

function PointsTable({ pointsLog }: { pointsLog: PointsEntry[] }) {
  return (
    <TableShell empty={pointsLog.length === 0} emptyIcon="ri-coins-line" emptyText="暂无积分流水" colSpan={6}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border bg-surface/50">
            <Th>变动</Th>
            <Th>变动前</Th>
            <Th>变动后</Th>
            <Th>来源</Th>
            <Th>关联ID</Th>
            <Th>时间</Th>
          </tr>
        </thead>
        <tbody>
          {pointsLog.map((p) => (
            <tr key={p.id} className="border-b border-surface-border hover:bg-surface/30 transition-colors h-9">
              <Td><span className={`font-mono font-semibold ${p.delta > 0 ? "text-emerald-500" : "text-red-500"}`}>{p.delta > 0 ? "+" : ""}{p.delta}</span></Td>
              <Td mono>{p.balance_after - p.delta}</Td>
              <Td mono>{p.balance_after}</Td>
              <Td>{reasonLabels[p.reason] || p.reason}</Td>
              <Td mono>{p.ref_id || "-"}</Td>
              <Td>{formatDateTime(p.created_at)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function SubscriptionOrdersTable({ orders }: { orders: SubscriptionOrderEntry[] }) {
  return (
    <TableShell empty={orders.length === 0} emptyIcon="ri-vip-crown-line" emptyText="暂无 VIP / 订阅订单" colSpan={8}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-surface-border bg-surface/50">
            <Th>订单号</Th>
            <Th>套餐</Th>
            <Th>金额</Th>
            <Th>支付状态</Th>
            <Th>订阅状态</Th>
            <Th>支付时间</Th>
            <Th>到期时间</Th>
            <Th>异常</Th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.order_id} className="border-b border-surface-border hover:bg-surface/30 transition-colors h-9">
              <Td mono>{o.order_id}</Td>
              <Td>{o.package_name || o.package_id}</Td>
              <Td mono>{o.currency} {Number(o.amount).toFixed(2)}</Td>
              <Td><OrderStatusBadge status={o.payment_status} label={o.payment_status_label} /></Td>
              <Td>{o.subscription_status_label}</Td>
              <Td>{formatDateTime(o.paid_at || o.order_created_at)}</Td>
              <Td>{formatDate(o.subscription_expires_at)}</Td>
              <Td>{o.abnormal_reason ? <span className="text-amber-600">{o.abnormal_reason}</span> : <span className="text-text-muted">-</span>}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableShell>
  );
}

function TableShell({
  empty,
  emptyIcon,
  emptyText,
  colSpan,
  children,
}: {
  empty: boolean;
  emptyIcon: string;
  emptyText: string;
  colSpan: number;
  children: ReactNode;
}) {
  if (empty) {
    return (
      <div className="bg-white rounded-lg border border-surface-border overflow-hidden">
        <table className="w-full text-sm">
          <tbody><tr><td colSpan={colSpan}><EmptyState icon={emptyIcon} text={emptyText} /></td></tr></tbody>
        </table>
      </div>
    );
  }
  return <div className="bg-white rounded-lg border border-surface-border overflow-x-auto">{children}</div>;
}

function EmptyState({ icon, text }: { icon: string; text: string }) {
  return (
    <div className="px-5 py-10 text-center text-text-muted">
      <i className={`${icon} text-3xl mb-2 block`} />
      {text}
    </div>
  );
}

function Th({ children, align = "left" }: { children: ReactNode; align?: "left" | "right" }) {
  return <th className={`px-5 py-3 text-text-secondary font-medium text-xs ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>;
}

function Td({ children, mono = false, align = "left" }: { children: ReactNode; mono?: boolean; align?: "left" | "right" }) {
  return <td className={`px-5 py-3.5 text-text-secondary ${mono ? "font-mono text-xs" : "text-sm"} ${align === "right" ? "text-right" : "text-left"}`}>{children}</td>;
}

function GenStatusBadge({ status }: { status: string }) {
  const config: Record<string, { cls: string; label: string }> = {
    created: { cls: "bg-slate-500/10 text-slate-600", label: "已创建" },
    queued: { cls: "bg-amber-500/10 text-amber-600", label: "排队中" },
    generating: { cls: "bg-brand/10 text-brand", label: "生成中" },
    auditing: { cls: "bg-cyan-500/10 text-cyan-600", label: "审核中" },
    success: { cls: "bg-emerald-500/10 text-emerald-500", label: "成功" },
    failed: { cls: "bg-red-500/10 text-red-500", label: "失败" },
    blocked: { cls: "bg-orange-500/10 text-orange-600", label: "已拦截" },
    timeout: { cls: "bg-red-500/10 text-red-600", label: "已超时" },
    cancelled: { cls: "bg-slate-500/10 text-slate-500", label: "已取消" },
    refunded: { cls: "bg-violet-500/10 text-violet-600", label: "已退款" },
    pending: { cls: "bg-amber-500/10 text-amber-500", label: "等待中" },
    processing: { cls: "bg-brand/10 text-brand", label: "处理中" },
  };
  const c = config[status] || { cls: "bg-text-muted/10 text-text-muted", label: status };
  return <Badge className={c.cls}>{c.label}</Badge>;
}

function OrderStatusBadge({ status, label }: { status: number; label: string }) {
  const cls = status === 1 ? "bg-emerald-500/10 text-emerald-500" : status === 2 ? "bg-red-500/10 text-red-500" : "bg-amber-500/10 text-amber-600";
  return <Badge className={cls}>{label}</Badge>;
}

function formatNumber(value: number | null | undefined): string {
  return Number(value || 0).toLocaleString();
}

function formatMoney(value: number | null | undefined): string {
  return `$${Number(value || 0).toFixed(2)}`;
}
